import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import { User } from "../types/auth";
import * as api from "../services/api";

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

function getInitialState(): AuthState {
  const token = localStorage.getItem("auth_token");
  const userStr = localStorage.getItem("auth_user");
  let user: User | null = null;

  try {
    if (userStr) user = JSON.parse(userStr);
  } catch {
    // corrupted data, just ignore it
  }

  return {
    user,
    token,
    loading: false,
    error: null,
    isAuthenticated: !!token && !!user,
  };
}

type AuthAction =
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "LOGIN_SUCCESS"; payload: { user: User; token: string } }
  | { type: "LOGOUT" };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case "LOGOUT":
      return {
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
}

interface AuthContextType {
  state: AuthState;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, undefined, getInitialState);

  // keep localStorage in sync whenever auth state changes
  useEffect(() => {
    if (state.token) {
      localStorage.setItem("auth_token", state.token);
    } else {
      localStorage.removeItem("auth_token");
    }
    if (state.user) {
      localStorage.setItem("auth_user", JSON.stringify(state.user));
    } else {
      localStorage.removeItem("auth_user");
    }
  }, [state.token, state.user]);

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const { token } = await api.login({ username, password });

        // the fake store API doesn't return user info with the token,
        // so we grab all users and match by username
        const users = await api.fetchUsers();
        const user = users.find((u) => u.username === username);

        if (!user) {
          dispatch({ type: "SET_ERROR", payload: "User not found." });
          return false;
        }

        dispatch({ type: "LOGIN_SUCCESS", payload: { user, token } });
        return true;
      } catch (err: any) {
        const message =
          err?.response?.data === "username or password is incorrect"
            ? "Invalid username or password."
            : "Login failed. Please try again.";
        dispatch({ type: "SET_ERROR", payload: message });
        return false;
      }
    },
    []
  );

  const logout = useCallback(() => {
    dispatch({ type: "LOGOUT" });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: "SET_ERROR", payload: null });
  }, []);

  return (
    <AuthContext.Provider value={{ state, login, logout, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
